import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/auth.hook";
import useHttp from "../../hooks/http.hook";
import "./BrowserLoginPage.css";

const TELEGRAM_BOT_URL = "https://t.me/QuokkaCrmBot";

function readError(result) {
    return result?.data?.error || result?.data?.message || "Не удалось выполнить запрос";
}

function TelegramIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
                fill="currentColor"
                d="M21.4 3.6 2.9 10.6c-1.2.5-1.2 1.1-.2 1.4l4.7 1.5 1.8 5.5c.2.7.1 1 .7.9.4 0 .6-.2.9-.4l2.5-2.4 5.2 3.8c.9.5 1.6.2 1.8-.9L22.8 4.8c.3-1.3-.5-1.9-1.4-1.2zM9.2 13.7l8.7-5.5c.4-.3.8-.1.5.2l-7.4 6.7-.3 3.2-1.5-4.6z"
            />
        </svg>
    );
}

function MailIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <path d="M4 7l8 6 8-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

const COPY = {
    login: {
        title: "Войти",
        text: "Владелец входит по почте, сотрудник — по имени пользователя.",
        submit: "Войти",
    },
    register: {
        title: "Регистрация",
        text: "Укажите почту — мы отправим код подтверждения.",
        submit: "Получить код",
        codeTitle: "Подтвердите почту",
        codeText: "Введите код из письма и придумайте пароль.",
        codeSubmit: "Зарегистрироваться",
    },
    reset: {
        title: "Сброс пароля",
        text: "Укажите почту — мы отправим код для нового пароля.",
        submit: "Получить код",
        codeTitle: "Новый пароль",
        codeText: "Введите код из письма и новый пароль.",
        codeSubmit: "Сохранить пароль",
    },
};

export default function BrowserLoginPage() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const { requestWithMeta } = useHttp();
    const emailSideRef = useRef(null);
    const [mode, setMode] = useState("login");
    const [step, setStep] = useState("email");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const needsCode = mode !== "login" && step === "code";
    const copy = COPY[mode];
    const title = needsCode ? copy.codeTitle : copy.title;
    const text = needsCode ? copy.codeText : copy.text;
    const submitLabel = needsCode ? copy.codeSubmit : copy.submit;

    const switchMode = (nextMode) => {
        setMode(nextMode);
        setStep("email");
        setPassword("");
        setCode("");
        setError("");
        setNotice("");
    };

    const openCabinet = (token) => {
        login(token);
        navigate("/", { replace: true });
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        if (submitting) return;

        setError("");
        setNotice("");
        setSubmitting(true);
        try {
            if (mode === "login") {
                const result = await requestWithMeta("/operator/login", "POST", {
                    contact: email.trim(),
                    password,
                });
                if (result?.data?.token) {
                    openCabinet(result.data.token);
                    return;
                }
                setError(readError(result));
                return;
            }

            if (!needsCode) {
                const path = mode === "register"
                    ? "/operator/registration"
                    : "/operator/reset-password";
                const result = await requestWithMeta(path, "POST", {
                    contact: email.trim(),
                });
                if (result?.ok) {
                    setStep("code");
                    setNotice("Код отправлен на почту");
                    return;
                }
                setError(readError(result));
                return;
            }

            const result = await requestWithMeta("/operator/verification", "POST", {
                verification: code.trim(),
                password,
            });
            if (result?.data?.token) {
                openCabinet(result.data.token);
                return;
            }
            setError(readError(result));
        } catch (requestError) {
            setError(requestError?.message || "Не удалось выполнить запрос");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="browser-login">
            <section className="browser-login-side browser-login-side--telegram">
                <div className="browser-login-sideInner">
                    <img
                        className="browser-login-logo"
                        src={`${process.env.PUBLIC_URL}/logo.png`}
                        alt="Квокка"
                    />
                    <span className="browser-login-kicker">
                        <TelegramIcon />
                        Telegram
                    </span>
                    <h1 className="browser-login-title">Войти через Telegram</h1>
                    <p className="browser-login-text">
                        Откройте бота и запустите приложение — так вы попадёте в кабинет.
                    </p>
                    <a className="browser-login-botButton" href={TELEGRAM_BOT_URL}>
                        Открыть бота
                    </a>
                </div>
                <div className="browser-login-stamp" aria-hidden="true">
                    <svg viewBox="0 0 200 200">
                        <defs>
                            <path id="browser-login-stampTop" d="M 100,100 m -64,0 a 64,64 0 1,1 128,0 a 64,64 0 1,1 -128,0" />
                            <path id="browser-login-stampBottom" d="M 100,100 m -64,0 a 64,64 0 1,0 128,0 a 64,64 0 1,0 -128,0" />
                            <filter id="browser-login-stampInk">
                                <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" result="noise" />
                                <feDisplacementMap in="SourceGraphic" in2="noise" scale="1.4" />
                            </filter>
                        </defs>
                        <g filter="url(#browser-login-stampInk)">
                            <circle cx="100" cy="100" r="90" className="browser-login-stampRingOuter" />
                            <circle cx="100" cy="100" r="85" className="browser-login-stampRing" />
                            <circle cx="100" cy="100" r="42" className="browser-login-stampRing" />
                            <text className="browser-login-stampArc" textAnchor="middle">
                                <textPath href="#browser-login-stampTop" startOffset="25%">
                                    Рекомендуем
                                </textPath>
                            </text>
                            <text className="browser-login-stampArc" textAnchor="middle">
                                <textPath href="#browser-login-stampBottom" startOffset="25%">
                                    этот способ
                                </textPath>
                            </text>
                            <path
                                className="browser-login-stampTelegram"
                                transform="translate(76 76) scale(2)"
                                d="M21.4 3.6 2.9 10.6c-1.2.5-1.2 1.1-.2 1.4l4.7 1.5 1.8 5.5c.2.7.1 1 .7.9.4 0 .6-.2.9-.4l2.5-2.4 5.2 3.8c.9.5 1.6.2 1.8-.9L22.8 4.8c.3-1.3-.5-1.9-1.4-1.2zM9.2 13.7l8.7-5.5c.4-.3.8-.1.5.2l-7.4 6.7-.3 3.2-1.5-4.6z"
                            />
                            <text x="36" y="104" textAnchor="middle" className="browser-login-stampDot">★</text>
                            <text x="164" y="104" textAnchor="middle" className="browser-login-stampDot">★</text>
                        </g>
                    </svg>
                </div>
                <button
                    className="browser-login-scrollButton"
                    type="button"
                    onClick={() => emailSideRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                >
                    Войти по почте или логину
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="m6 9 6 6 6-6" />
                    </svg>
                </button>
            </section>

            <section ref={emailSideRef} className="browser-login-side browser-login-side--email">
                <form className="browser-login-form" onSubmit={onSubmit}>
                    <span className="browser-login-kicker browser-login-kicker--dark">
                        <MailIcon />
                        {mode === "login" ? "Кабинет" : "Почта"}
                    </span>
                    <h1 className="browser-login-title">{title}</h1>
                    <p className="browser-login-text browser-login-text--dark">{text}</p>

                    <label className="browser-login-label" htmlFor="browser-login-email">
                        {mode === "login" ? "Логин" : "Почта"}
                    </label>
                    <input
                        id="browser-login-email"
                        className="browser-login-input"
                        type={mode === "login" ? "text" : "email"}
                        name={mode === "login" ? "username" : "email"}
                        autoComplete={mode === "login" ? "username" : "email"}
                        inputMode={mode === "login" ? "text" : "email"}
                        placeholder={mode === "login" ? "почта или имя пользователя" : "you@example.com"}
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        disabled={needsCode || submitting}
                        required
                    />

                    {needsCode ? (
                        <>
                            <label className="browser-login-label" htmlFor="browser-login-code">
                                Код
                            </label>
                            <input
                                id="browser-login-code"
                                className="browser-login-input"
                                type="text"
                                name="verification"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                placeholder="123456"
                                value={code}
                                onChange={(event) => setCode(event.target.value)}
                                disabled={submitting}
                                required
                            />
                        </>
                    ) : null}

                    {mode === "login" || needsCode ? (
                        <>
                            <label className="browser-login-label" htmlFor="browser-login-password">
                                Пароль
                            </label>
                            <input
                                id="browser-login-password"
                                className="browser-login-input"
                                type="password"
                                name="password"
                                autoComplete={mode === "login" ? "current-password" : "new-password"}
                                placeholder="Пароль"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                disabled={submitting}
                                required
                            />
                        </>
                    ) : null}

                    {error ? <p className="browser-login-error">{error}</p> : null}
                    {notice ? <p className="browser-login-notice">{notice}</p> : null}

                    <button className="browser-login-submit" type="submit" disabled={submitting}>
                        {submitting ? "Подождите…" : submitLabel}
                    </button>

                    <div className="browser-login-links">
                        {mode === "login" ? (
                            <>
                                <button type="button" className="browser-login-link" onClick={() => switchMode("register")}>
                                    Нет аккаунта? Зарегистрироваться
                                </button>
                                <button type="button" className="browser-login-link" onClick={() => switchMode("reset")}>
                                    Забыли пароль?
                                </button>
                            </>
                        ) : (
                            <>
                                {needsCode ? (
                                    <button
                                        type="button"
                                        className="browser-login-link"
                                        onClick={() => {
                                            setStep("email");
                                            setCode("");
                                            setPassword("");
                                            setNotice("");
                                            setError("");
                                        }}
                                    >
                                        Изменить почту
                                    </button>
                                ) : null}
                                <button type="button" className="browser-login-link" onClick={() => switchMode("login")}>
                                    Уже есть аккаунт? Войти
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </section>
        </div>
    );
}
